import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Galleria } from "primereact/galleria";
import { URLS } from "../Url";
import {
  Sparkles,
  ArrowRight,
  MessageCircle,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Star,
} from "lucide-react";

// Curated authentic fallback moments if API has fewer than 6
const FALLBACK_HOME_MOMENTS = [
  {
    _id: "h_sm_1",
    image: "uploads/galleryImg/1776006174259-059d6562-b97e-4b7d-93a4-8beab3767fcb.jpg",
    title: "Bride to Be Luxury Suite",
    label: "VIP Exclusive",
  },
  {
    _id: "h_sm_2",
    image: "uploads/galleryImg/1774802984680-WhatsApp Image 2026-03-29 at 10.19.07 PM.jpeg",
    title: "Star Moments Group Celebration",
    label: "Celebrity Gathering",
  },
  {
    _id: "h_sm_3",
    image: "uploads/galleryImg/1774802474665-WhatsApp Image 2026-03-29 at 10.10.46 PM.jpeg",
    title: "Overhead Theatre Celebration",
    label: "Star Memories",
  },
  {
    _id: "h_sm_4",
    image: "uploads/galleryImg/1773169469457-WhatsApp Image 2026-03-10 at 11.10.41 PM (2) (1).jpeg",
    title: "Star Glasses Birthday Surprise",
    label: "Special Guest",
  },
  {
    _id: "h_sm_5",
    image: "uploads/galleryImg/1773169212106-WhatsApp Image 2026-03-10 at 11.15.03 PM.jpeg",
    title: "Private Theatre Besties Celebration",
    label: "Star Spotlight",
  },
  {
    _id: "h_sm_6",
    image: "uploads/galleryImg/1773169199302-WhatsApp Image 2026-03-10 at 11.10.26 PM (4).jpeg",
    title: "24th Birthday LED Lights Affair",
    label: "VIP Milestone",
  },
];

const SPOTLIGHT_CYCLE_MS = 3600; // 3.6s per sequential highlight

export default function GallerySection({ images }) {
  const galleriaRef = useRef(null);
  const [activeGal, setActiveGal] = useState(0);
  const [activeSpotlightIdx, setActiveSpotlightIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const touchStartX = useRef(null);

  const displayList = useMemo(() => {
    if (Array.isArray(images) && images.length > 0) {
      return images.slice(0, 6).map((item, idx) => {
        const fallback = FALLBACK_HOME_MOMENTS[idx % FALLBACK_HOME_MOMENTS.length];
        return {
          ...item,
          title: item.title || fallback.title,
          label: item.label || fallback.label,
        };
      });
    }
    return FALLBACK_HOME_MOMENTS;
  }, [images]);

  // Sequential spotlight animation timer
  useEffect(() => {
    if (!isPlaying || isHovered) return;

    const timer = setInterval(() => {
      setActiveSpotlightIdx((prev) => (prev + 1) % displayList.length);
    }, SPOTLIGHT_CYCLE_MS);

    return () => clearInterval(timer);
  }, [isPlaying, isHovered, displayList.length]);

  const handleNext = useCallback(() => {
    setActiveSpotlightIdx((prev) => (prev + 1) % displayList.length);
  }, [displayList.length]);

  const handlePrev = useCallback(() => {
    setActiveSpotlightIdx((prev) => (prev - 1 + displayList.length) % displayList.length);
  }, [displayList.length]);

  const togglePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const getFullImgUrl = (item) => {
    if (!item || !item.image) return "";
    if (item.image.startsWith("http://") || item.image.startsWith("https://")) {
      return encodeURI(item.image);
    }
    const cleanPath = item.image.startsWith("/") ? item.image.slice(1) : item.image;
    return `https://api.carnivalcastle.com/${encodeURI(cleanPath)}`;
  };

  const openLightbox = useCallback((idx) => {
    setActiveGal(idx);
    galleriaRef.current?.show();
  }, []);

  // Touch Swipe Handling for Mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (diff > 45) {
      handleNext();
    } else if (diff < -45) {
      handlePrev();
    }
    touchStartX.current = null;
  };

  const activePhoto = displayList[activeSpotlightIdx] || displayList[0];

  // PrimeReact Fullscreen Lightbox Template
  const galItem = useCallback(
    (item) => (
      <div className="h3d-lightbox-wrap">
        <img
          src={getFullImgUrl(item)}
          alt={item.title || "Carnival Castle Celebration Moment"}
          className="h3d-lightbox-img"
        />
        <div className="h3d-lightbox-caption">
          <span className="h3d-lb-badge">✦ STAR MOMENT ✦</span>
          <h4 className="h3d-lb-title">{item.title || "Celebrated at Carnival Castle"}</h4>
          <span className="h3d-lb-sub">100% Private Theatre Experience • Hyderabad</span>
        </div>
      </div>
    ),
    []
  );

  return (
    <section className="h3d-section" id="gallery">
      <div className="container">
        {/* Compact Editorial Header (Zero bloated padding) */}
        <div className="h3d-header text-center">
          <span className="h3d-badge">✦ CELEBS ATTENDED ✦</span>
          <h2 className="h3d-title">
            STAR <span>MOMENTS</span>
          </h2>
          <p className="h3d-subtitle">
            Celebrating the stars who made their moments at Carnival Castle.
          </p>
          <div className="h3d-divider" />
        </div>

        {/* ── LUXURY DIRECTOR CONTROL BAR ── */}
        <div className="h3d-director-bar">
          <div className="h3d-director-left">
            <span className="h3d-live-beacon" />
            <span className="h3d-director-tag">SPOTLIGHT:</span>
            <span className="h3d-director-name">
              #{String(activeSpotlightIdx + 1).padStart(2, "0")} —{" "}
              <strong>{activePhoto?.title}</strong>
            </span>
          </div>

          <div className="h3d-director-right">
            <button
              type="button"
              className="h3d-arrow-btn"
              onClick={handlePrev}
              title="Previous Moment"
              aria-label="Previous Moment"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              className={`h3d-play-btn ${isPlaying ? "is-playing" : ""}`}
              onClick={togglePlayPause}
              title={isPlaying ? "Pause Tour" : "Play Tour"}
              aria-label={isPlaying ? "Pause Tour" : "Play Tour"}
            >
              {isPlaying ? <Pause size={12} /> : <Play size={12} />}
              <span>{isPlaying ? "Pause" : "Play"}</span>
            </button>
            <button
              type="button"
              className="h3d-arrow-btn"
              onClick={handleNext}
              title="Next Moment"
              aria-label="Next Moment"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* ── GRAND 3D PERSPECTIVE STAGE (EXPANDED CARDS, 100% UNCLIPPED, NO EMPTY SPACES) ── */}
        <div
          className="h3d-stage-viewport"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="h3d-stage-track">
            {displayList.map((item, idx) => {
              const count = displayList.length;
              let diff = idx - activeSpotlightIdx;
              while (diff > count / 2) diff -= count;
              while (diff < -count / 2) diff += count;

              const isSpotlighted = diff === 0;
              const absDiff = Math.abs(diff);
              const sign = diff > 0 ? 1 : -1;
              const imgSrc = getFullImgUrl(item);

              // Calculate 3D stage layout parameters via CSS variables for seamless responsiveness
              let transformStyle = "";
              let opacityStyle = 1;
              let zIndexStyle = 10;
              let pointerEventsStyle = "auto";

              if (diff === 0) {
                transformStyle =
                  "translateX(-50%) translateY(-50%) translateZ(95px) rotateY(0deg) scale(1.05)";
                opacityStyle = 1;
                zIndexStyle = 10;
              } else if (absDiff === 1) {
                transformStyle = `translateX(calc(-50% + ${sign} * var(--h3d-step-1))) translateY(-50%) translateZ(-40px) rotateY(calc(${-sign} * var(--h3d-rot-1))) scale(0.85)`;
                opacityStyle = 0.85;
                zIndexStyle = 7;
              } else if (absDiff === 2) {
                transformStyle = `translateX(calc(-50% + ${sign} * var(--h3d-step-2))) translateY(-50%) translateZ(-115px) rotateY(calc(${-sign} * var(--h3d-rot-2))) scale(0.70)`;
                opacityStyle = 0.5;
                zIndexStyle = 5;
              } else {
                transformStyle = `translateX(calc(-50% + ${sign} * var(--h3d-step-3))) translateY(-50%) translateZ(-210px) rotateY(calc(${-sign} * var(--h3d-rot-3))) scale(0.5)`;
                opacityStyle = 0;
                zIndexStyle = 1;
                pointerEventsStyle = "none";
              }

              return (
                <div
                  key={item._id || idx}
                  className={`h3d-card-pod ${isSpotlighted ? "is-spotlighted" : ""}`}
                  style={{
                    transform: transformStyle,
                    opacity: opacityStyle,
                    zIndex: zIndexStyle,
                    pointerEvents: pointerEventsStyle,
                  }}
                  onClick={() => {
                    if (isSpotlighted) {
                      openLightbox(idx);
                    } else {
                      setActiveSpotlightIdx(idx);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Star Moment ${idx + 1}: ${item.title}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      if (isSpotlighted) openLightbox(idx);
                      else setActiveSpotlightIdx(idx);
                    }
                  }}
                >
                  {/* Top Edge Golden Countdown Progress Bar (Active Card Only) */}
                  {isSpotlighted && (
                    <div className="h3d-timer-track">
                      <div
                        key={`timer-${idx}-${isPlaying}-${isHovered}`}
                        className={`h3d-timer-fill ${isPlaying && !isHovered ? "is-running" : "is-halted"
                          }`}
                        style={{ animationDuration: `${SPOTLIGHT_CYCLE_MS}ms` }}
                      />
                    </div>
                  )}

                  {/* 100% Unclipped Natural Photo (Zero Empty Padding, Snug Frame) */}
                  <div className="h3d-photo-frame">
                    <img
                      src={imgSrc}
                      alt={item.title}
                      loading="lazy"
                      className="h3d-photo-img"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/img/a1.jpg";
                      }}
                    />

                    {/* VIP Corner Tag */}
                    <div className="h3d-corner-badge">
                      {isSpotlighted ? (
                        <span className="h3d-badge-vip">
                          <Star size={10} fill="#F5D87A" />
                          <span>VIP MOMENT</span>
                        </span>
                      ) : (
                        <span className="h3d-badge-num">#{String(idx + 1).padStart(2, "0")}</span>
                      )}
                    </div>

                    {/* Scrim Overlay with Title & Expand Icon */}
                    <div className="h3d-photo-scrim">
                      <span className="h3d-card-label">{item.label}</span>
                      <h3 className="h3d-card-title">{item.title}</h3>
                      {isSpotlighted && (
                        <span className="h3d-expand-hint">
                          <Maximize2 size={11} />
                          <span>Tap to View Fullscreen</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── SYNCHRONIZED CELEBRITY MOMENTS DOCK (ALL 6 VISIBLE AT ONCE) ── */}
        <div className="h3d-dock-strip" role="tablist" aria-label="Celebrity moments selector">
          {displayList.map((item, dotIdx) => {
            const isActive = dotIdx === activeSpotlightIdx;
            const imgSrc = getFullImgUrl(item);

            return (
              <button
                key={dotIdx}
                type="button"
                className={`h3d-dock-item ${isActive ? "is-active" : ""}`}
                onClick={() => setActiveSpotlightIdx(dotIdx)}
                title={`Moment ${dotIdx + 1}: ${item.title}`}
                role="tab"
                aria-selected={isActive}
              >
                <div className="h3d-dock-thumb">
                  <img src={imgSrc} alt="" onError={(e) => (e.target.src = "/img/a1.jpg")} />
                </div>
                <span className="h3d-dock-num">{String(dotIdx + 1).padStart(2, "0")}</span>
                <span className="h3d-dock-label">{item.title}</span>
                {isActive && <span className="h3d-dock-beacon" />}
              </button>
            );
          })}
        </div>

        {/* PrimeReact Fullscreen Lightbox Galleria */}
        <Galleria
          ref={galleriaRef}
          value={displayList}
          activeIndex={activeGal}
          onItemChange={(e) => setActiveGal(e.index)}
          circular
          fullScreen
          showItemNavigators
          showThumbnails={false}
          item={galItem}
        />

        {/* Compact Action CTAs */}
        <div className="h3d-bottom-bar">
          <a href="/gallery" className="h3d-btn-all">
            <span>Explore All 17+ Star Moments</span>
            <ArrowRight size={13} />
          </a>
          <a href="/locations" className="h3d-btn-book">
            <span>Book Your Star Experience</span>
            <Sparkles size={12} />
          </a>
          <a
            href="https://wa.me/918341428342"
            target="_blank"
            rel="noopener noreferrer"
            className="h3d-btn-wa"
          >
            <MessageCircle size={13} />
            <span>Chat On WhatsApp</span>
          </a>
        </div>
      </div>

      {/* ── LUXURY 3D STAGE STYLES ── */}
      <style>{`
        /* ── SECTION ROOT: BALANCED LUXURY PADDING ── */
        .h3d-section {
          padding: 24px 0 26px;
          background: #FAF7F2;
          position: relative;
          overflow: hidden;
        }

        .h3d-header {
          margin-bottom: 10px;
        }

        .h3d-badge {
          display: inline-block;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 2.2px;
          text-transform: uppercase;
          color: #8C6A1E;
          background: rgba(201, 168, 76, 0.12);
          border: 1px solid rgba(201, 168, 76, 0.32);
          padding: 3.5px 13px;
          border-radius: 30px;
          margin-bottom: 5px;
        }

        .h3d-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(23px, 3.4vw, 34px);
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: #1A0030;
          margin: 0 0 3px;
          line-height: 1.15;
        }

        .h3d-title span {
          background: linear-gradient(135deg, #5E0E73 0%, #8E24AA 50%, #C9A84C 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .h3d-subtitle {
          font-size: 13.5px;
          color: #6B5B73;
          margin: 0 auto 6px;
          line-height: 1.4;
          max-width: 520px;
        }

        .h3d-divider {
          width: 44px;
          height: 1.5px;
          background: linear-gradient(90deg, transparent, #C9A84C, transparent);
          margin: 0 auto;
        }

        /* ── DIRECTOR CONTROL BAR ── */
        .h3d-director-bar {
          background: linear-gradient(135deg, #18002B 0%, #2A0444 55%, #18002B 100%);
          border: 1.5px solid rgba(245, 216, 122, 0.4);
          border-radius: 12px;
          padding: 7px 15px;
          margin-bottom: 14px;
          box-shadow: 0 4px 18px rgba(26, 0, 48, 0.1);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
        }

        .h3d-director-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .h3d-live-beacon {
          width: 6.5px;
          height: 6.5px;
          background: #F5D87A;
          border-radius: 50%;
          box-shadow: 0 0 8px #F5D87A;
          animation: h3dPulse 1.4s ease-in-out infinite;
        }

        @keyframes h3dPulse {
          0%, 100% {
            transform: scale(0.85);
            opacity: 0.7;
          }
          50% {
            transform: scale(1.35);
            opacity: 1;
          }
        }

        .h3d-director-tag {
          font-size: 9.5px;
          font-weight: 800;
          letter-spacing: 0.8px;
          color: #F5D87A;
          text-transform: uppercase;
        }

        .h3d-director-name {
          font-size: 12.5px;
          color: #FFFFFF;
        }

        .h3d-director-name strong {
          color: #F5D87A;
        }

        .h3d-director-right {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .h3d-arrow-btn {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(245, 216, 122, 0.3);
          color: #FFFFFF;
          width: 26px;
          height: 26px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 0;
        }

        .h3d-arrow-btn:hover {
          background: rgba(245, 216, 122, 0.25);
          border-color: #F5D87A;
          color: #F5D87A;
        }

        .h3d-play-btn {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(245, 216, 122, 0.3);
          color: #FFFFFF;
          border-radius: 12px;
          padding: 3.5px 10px;
          font-size: 10.5px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s ease;
        }

        .h3d-play-btn.is-playing {
          background: linear-gradient(135deg, rgba(201, 168, 76, 0.3) 0%, rgba(94, 14, 115, 0.3) 100%);
          border-color: #F5D87A;
          color: #F5D87A;
        }

        /* ── GRAND 3D PERSPECTIVE STAGE VIEWPORT (INCREASED CARD SIZE, CINEMATIC PRESENCE) ── */
        .h3d-stage-viewport {
          position: relative;
          width: 100%;
          height: 490px;
          perspective: 1250px;
          perspective-origin: 50% 50%;
          overflow: visible;
          user-select: none;
          touch-action: pan-y;
          --h3d-step-1: 320px;
          --h3d-step-2: 600px;
          --h3d-step-3: 790px;
          --h3d-rot-1: 22deg;
          --h3d-rot-2: 38deg;
          --h3d-rot-3: 50deg;
        }

        .h3d-stage-track {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
        }

        /* ── EXPANDED CARD SIZE: GRAND, BEAUTIFULLY PROPORTIONED, ZERO CROPPING ── */
        .h3d-card-pod {
          position: absolute;
          top: 50%;
          left: 50%;
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          max-height: 450px;
          max-width: min(600px, 88vw);
          width: auto;
          border-radius: 20px;
          overflow: hidden;
          background: #140220;
          border: 1.5px solid rgba(201, 168, 76, 0.32);
          box-shadow: 0 14px 42px rgba(0, 0, 0, 0.38);
          outline: none;
          cursor: pointer;
          transition: transform 0.55s cubic-bezier(0.16, 1, 0.3, 1),
                      opacity 0.45s ease,
                      box-shadow 0.45s ease,
                      border-color 0.45s ease;
          transform-style: preserve-3d;
          will-change: transform, opacity;
        }

        .h3d-card-pod:hover {
          border-color: rgba(245, 216, 122, 0.85);
          box-shadow: 0 16px 44px rgba(0, 0, 0, 0.45), 0 0 25px rgba(245, 216, 122, 0.35);
        }

        .h3d-card-pod.is-spotlighted {
          border: 2px solid #F5D87A;
          box-shadow: 0 20px 52px rgba(0, 0, 0, 0.55), 0 0 32px rgba(245, 216, 122, 0.5);
        }

        /* Active Card Golden Countdown Timer */
        .h3d-timer-track {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: rgba(255, 255, 255, 0.2);
          z-index: 15;
          overflow: hidden;
        }

        .h3d-timer-fill {
          height: 100%;
          width: 0%;
          background: linear-gradient(90deg, #F5D87A 0%, #DFBD69 60%, #FFFFFF 100%);
          box-shadow: 0 0 8px rgba(245, 216, 122, 0.95);
        }

        .h3d-timer-fill.is-running {
          animation: h3dTimer 3.6s linear forwards;
        }

        .h3d-timer-fill.is-halted {
          animation-play-state: paused;
        }

        @keyframes h3dTimer {
          from { width: 0%; }
          to { width: 100%; }
        }

        /* Snug Photo Frame (Zero Empty Letterbox Padding, Zero Blur) */
        .h3d-photo-frame {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          max-height: 450px;
          max-width: min(600px, 88vw);
          width: auto;
          overflow: hidden;
          line-height: 0;
        }

        /* 100% Unclipped Image (Complete Image Visible) */
        .h3d-photo-img {
          width: auto;
          height: auto;
          max-height: 450px;
          max-width: min(600px, 88vw);
          display: block;
          object-fit: contain;
          transition: transform 0.5s ease;
        }

        .h3d-card-pod:hover .h3d-photo-img {
          transform: scale(1.025);
        }

        /* Corner VIP / Index Badge */
        .h3d-corner-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          z-index: 8;
          pointer-events: none;
          line-height: 1;
        }

        .h3d-badge-vip {
          background: linear-gradient(135deg, rgba(26, 0, 48, 0.95) 0%, rgba(53, 10, 84, 0.95) 100%);
          border: 1px solid #F5D87A;
          color: #F5D87A;
          font-size: 8.5px;
          font-weight: 800;
          letter-spacing: 0.8px;
          padding: 2.5px 7px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          gap: 3.5px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
        }

        .h3d-badge-num {
          background: rgba(0, 0, 0, 0.65);
          color: rgba(245, 216, 122, 0.95);
          font-size: 8.5px;
          font-weight: 800;
          padding: 2px 6px;
          border-radius: 9px;
          border: 1px solid rgba(245, 216, 122, 0.28);
          backdrop-filter: blur(4px);
        }

        /* Scrim Overlay with Caption */
        .h3d-photo-scrim {
          position: absolute;
          inset: auto 0 0 0;
          background: linear-gradient(to top, rgba(16, 0, 28, 0.95) 0%, rgba(16, 0, 28, 0.6) 65%, transparent 100%);
          padding: 26px 12px 10px;
          z-index: 6;
          display: flex;
          flex-direction: column;
          gap: 2px;
          line-height: 1.25;
          pointer-events: none;
        }

        .h3d-card-label {
          font-size: 8.5px;
          font-weight: 800;
          letter-spacing: 0.7px;
          color: #DFBD69;
          text-transform: uppercase;
        }

        .h3d-card-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 700;
          color: #FFFFFF;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.85);
        }

        .h3d-expand-hint {
          font-size: 9px;
          font-weight: 700;
          color: #F5D87A;
          display: inline-flex;
          align-items: center;
          gap: 3.5px;
          margin-top: 3px;
        }

        /* ── SYNCHRONIZED MOMENTS DOCK (ALL 6 VISIBLE AT ONCE) ── */
        .h3d-dock-strip {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          margin-top: 16px;
          flex-wrap: wrap;
        }

        .h3d-dock-item {
          background: #FFFFFF;
          border: 1px solid rgba(201, 168, 76, 0.25);
          border-radius: 20px;
          padding: 3px 9px 3px 3px;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          transition: all 0.25s ease;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
          outline: none;
        }

        .h3d-dock-item:hover {
          border-color: #F5D87A;
          transform: translateY(-2px);
          box-shadow: 0 4px 10px rgba(201, 168, 76, 0.2);
        }

        .h3d-dock-item.is-active {
          background: linear-gradient(135deg, #18002B 0%, #30044E 100%);
          border-color: #F5D87A;
          box-shadow: 0 0 10px rgba(245, 216, 122, 0.35);
          transform: translateY(-2px);
        }

        .h3d-dock-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          overflow: hidden;
          border: 1px solid rgba(201, 168, 76, 0.5);
          flex-shrink: 0;
        }

        .h3d-dock-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .h3d-dock-num {
          font-size: 10px;
          font-weight: 800;
          color: #8C6A1E;
        }

        .h3d-dock-item.is-active .h3d-dock-num {
          color: #F5D87A;
        }

        .h3d-dock-label {
          font-size: 10.5px;
          font-weight: 700;
          color: #4A3B52;
          max-width: 110px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .h3d-dock-item.is-active .h3d-dock-label {
          color: #FFFFFF;
        }

        .h3d-dock-beacon {
          width: 5px;
          height: 5px;
          background: #F5D87A;
          border-radius: 50%;
          box-shadow: 0 0 5px #F5D87A;
          margin-left: 2px;
        }

        /* ── LIGHTBOX CUSTOM STYLING ── */
        .h3d-lightbox-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          max-height: 88vh;
        }

        .h3d-lightbox-img {
          max-width: 90vw;
          max-height: 76vh;
          width: auto;
          height: auto;
          display: block;
          object-fit: contain;
          border-radius: 10px;
          border: 1px solid rgba(201, 168, 76, 0.35);
          box-shadow: 0 14px 55px rgba(0, 0, 0, 0.85);
        }

        .h3d-lightbox-caption {
          margin-top: 12px;
          text-align: center;
          color: #FFFFFF;
        }

        .h3d-lb-badge {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1.5px;
          color: #C9A84C;
          display: inline-block;
          margin-bottom: 3px;
        }

        .h3d-lb-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 16px;
          font-weight: 700;
          margin: 0;
          color: #FFFFFF;
        }

        .h3d-lb-sub {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
        }

        /* ── COMPACT BOTTOM ACTION BUTTONS ── */
        .h3d-bottom-bar {
          display: flex;
          gap: 10px;
          justify-content: center;
          align-items: center;
          margin-top: 16px;
          flex-wrap: wrap;
        }

        .h3d-btn-all {
          background: linear-gradient(135deg, #4A154B 0%, #1A0030 100%);
          color: #FFFFFF !important;
          font-weight: 700;
          font-size: 12.5px;
          padding: 8px 18px;
          border-radius: 20px;
          text-decoration: none !important;
          border: 1px solid rgba(201, 168, 76, 0.35);
          box-shadow: 0 3px 12px rgba(74, 21, 75, 0.25);
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .h3d-btn-all:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 18px rgba(74, 21, 75, 0.35);
          border-color: rgba(201, 168, 76, 0.7);
          color: #F5D87A !important;
        }

        .h3d-btn-book {
          background: linear-gradient(135deg, #DFBD69 0%, #B89535 100%);
          color: #1A0030 !important;
          font-weight: 800;
          font-size: 12.5px;
          padding: 8px 16px;
          border-radius: 20px;
          text-decoration: none !important;
          box-shadow: 0 3px 12px rgba(201, 168, 76, 0.35);
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }

        .h3d-btn-book:hover {
          background: linear-gradient(135deg, #FFF0B8 0%, #C9A84C 100%);
          transform: translateY(-2px);
          box-shadow: 0 5px 18px rgba(201, 168, 76, 0.5);
          color: #1A0030 !important;
        }

        .h3d-btn-wa {
          background: #25D366;
          color: #FFFFFF !important;
          font-weight: 700;
          font-size: 12.5px;
          padding: 8px 15px;
          border-radius: 20px;
          text-decoration: none !important;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          transition: all 0.2s ease;
          box-shadow: 0 3px 10px rgba(37, 211, 102, 0.28);
        }

        .h3d-btn-wa:hover {
          background: #20BA5A;
          transform: translateY(-2px);
          color: #FFFFFF !important;
        }

        /* ── TABLET RESPONSIVE RULES (576px - 991px) ── */
        @media (max-width: 991px) {
          .h3d-stage-viewport {
            height: 440px;
            --h3d-step-1: 230px;
            --h3d-step-2: 430px;
            --h3d-step-3: 570px;
            --h3d-rot-1: 20deg;
            --h3d-rot-2: 34deg;
            --h3d-rot-3: 45deg;
          }
          .h3d-card-pod {
            max-height: 400px;
            max-width: min(500px, 86vw);
            border-radius: 17px;
          }
          .h3d-photo-frame, .h3d-photo-img {
            max-height: 400px;
            max-width: min(500px, 86vw);
          }
          .h3d-dock-label {
            max-width: 90px;
          }
        }

        /* ── MOBILE RESPONSIVE RULES (< 576px) ── */
        @media (max-width: 575px) {
          .h3d-section {
            padding: 18px 0 20px;
          }
          .h3d-header {
            margin-bottom: 8px;
          }
          .h3d-director-bar {
            padding: 6px 12px;
            gap: 6px;
            margin-bottom: 12px;
          }
          .h3d-director-left {
            width: 100%;
          }
          .h3d-director-name {
            font-size: 11.5px;
          }
          /* Generously Sized Stage on Mobile */
          .h3d-stage-viewport {
            height: 390px;
            --h3d-step-1: 120px;
            --h3d-step-2: 225px;
            --h3d-step-3: 300px;
            --h3d-rot-1: 16deg;
            --h3d-rot-2: 30deg;
            --h3d-rot-3: 40deg;
          }
          /* Bolder, larger card size on mobile */
          .h3d-card-pod {
            max-height: 340px;
            max-width: min(330px, 84vw);
            border-radius: 15px;
          }
          .h3d-photo-frame, .h3d-photo-img {
            max-height: 340px;
            max-width: min(330px, 84vw);
          }
          .h3d-card-title {
            font-size: 11.5px;
          }
          /* Horizontal Smooth Scrolling Dock on Mobile */
          .h3d-dock-strip {
            display: flex;
            flex-wrap: nowrap;
            overflow-x: auto;
            justify-content: flex-start;
            padding: 4px 6px;
            gap: 6px;
            margin-top: 12px;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .h3d-dock-strip::-webkit-scrollbar {
            display: none;
          }
          .h3d-dock-item {
            flex-shrink: 0;
            padding: 3px 8px 3px 3px;
          }
          .h3d-dock-label {
            display: inline-block;
            max-width: 80px;
            font-size: 9.5px;
          }
          .h3d-bottom-bar {
            flex-direction: column;
            width: 100%;
            margin-top: 14px;
            gap: 8px;
          }
          .h3d-btn-all, .h3d-btn-book, .h3d-btn-wa {
            width: 100%;
            justify-content: center;
            padding: 10px 16px;
            font-size: 12.5px;
          }
        }

        /* ── ACCESSIBILITY: RESPECT PREFERS-REDUCED-MOTION ── */
        @media (prefers-reduced-motion: reduce) {
          .h3d-timer-fill,
          .h3d-card-pod,
          .h3d-photo-img,
          .h3d-live-beacon {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </section>
  );
}